import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PublicLayout } from '../layouts/PublicLayout';
import { AppLayout } from '../layouts/AppLayout';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { PhoneBook } from '../pages/PhoneBook';
import { Login } from '../pages/Login';
import { SignUp } from '../pages/SignUp';
import { ContactUs } from '../pages/ContactUs';
import { PrivacyPolicy } from '../pages/PrivacyPolicy';
import { RequestRemoval } from '../pages/RequestRemoval';
import { PendingApproval } from '../pages/PendingApproval';
import { NotFound } from '../pages/NotFound';
import { EmployeeDashboard } from '../pages/employee/Dashboard';
import { AddNumber } from '../pages/employee/AddNumber';
import { EmployeeRequests } from '../pages/employee/Requests';
import { EmployeeUserApprovals } from '../pages/employee/UserApprovals';
import { EmployeeContacts } from '../pages/employee/Contacts';
import { AddContactRequest } from '../pages/AddContactRequest';
import { AdminDashboard } from '../pages/admin/Dashboard';
import { AdminContacts } from '../pages/admin/Contacts';
import { AdminRequests } from '../pages/admin/Requests';
import { AdminEmployees } from '../pages/admin/Employees';
import { AdminUsers } from '../pages/admin/Users';
import { AdminAuditLogs } from '../pages/admin/AuditLogs';
import { AdminShivars } from '../pages/admin/Shivars';
import { AdminContactMessages } from '../pages/admin/ContactMessages';
import { AdminRemovalRequests } from '../pages/admin/RemovalRequests';
import { AdminContactChanges } from '../pages/admin/ContactChanges';
import { AdminAudit } from '../pages/admin/Audit';
import { ResetPassword } from '../pages/ResetPassword';
import { EditContact } from '../pages/EditContact';

export const AppRouter = () => {
  const { role, loading } = useAuth();

  if (loading) return null;

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<PhoneBook />} />
        <Route path="/phone-book" element={<PhoneBook />} />
        <Route path="/add-contact" element={<AddContactRequest />} />
        <Route path="/login" element={role ? <Navigate to={dashboardFor(role)} replace /> : <Login />} />
        <Route path="/signup" element={role ? <Navigate to={dashboardFor(role)} replace /> : <SignUp />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/request-removal" element={<RequestRemoval />} />
        <Route path="/pending-approval" element={<PendingApproval />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Route>

      <Route element={<AppLayout />}>        <Route
          path="/employee"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <EmployeeDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/add-number"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <AddNumber />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/contacts"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <EmployeeContacts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/requests"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <EmployeeRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/user-approvals"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <EmployeeUserApprovals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-contact/:id"
          element={
            <ProtectedRoute allowed={['EMPLOYEE', 'ADMIN']}>
              <EditContact />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route element={<AppLayout />}>
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/contacts"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminContacts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/employees"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminEmployees />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit-logs"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminAuditLogs />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/contact-changes"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminContactChanges />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/shivars"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminShivars />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/contact-messages"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminContactMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/removal-requests"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminRemovalRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute allowed={['ADMIN']}>
              <AdminAudit />
            </ProtectedRoute>
          }
        />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const dashboardFor = (role: string | null): string => {
  if (role === 'ADMIN' || role === 'SUPERADMIN') return '/admin';
  if (role === 'EMPLOYEE') return '/employee';
  return '/phone-book';
};
